// Primitivos de UI compartilhados pelas telas do G17.
// Extraído de App.js sem alteração de comportamento.
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTheme, useIsMobile } from "../lib/theme";
import { CONTEST_EMBLEMS, CONTEST_LIST, STAR_LEVELS } from "../lib/constants";

export function KnowledgeDots({ level }) {
  const t = useTheme();
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {[1,2,3,4,5].map(i => (
        <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: i <= level ? t.ac : t.bgInner, border: `1px solid ${i <= level ? t.ac : t.border}` }} />
      ))}
    </div>
  );
}

export function MiniBar({ value, max, color }) {
  const t = useTheme();
  return (
    <div style={{ flex: 1, background: t.bgInner, borderRadius: 99, height: 5, overflow: "hidden" }}>
      <div style={{ width: `${(value/max)*100}%`, height: "100%", background: color, borderRadius: 99 }} />
    </div>
  );
}

export function Toggle({ on, onChange }) {
  const t = useTheme();
  return (
    <div onClick={() => onChange(!on)} style={{ width: 36, height: 20, borderRadius: 99, background: on ? t.ac : t.border, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ width: 14, height: 14, borderRadius: "50%", background: on ? (t.mode === "dark" ? "#0a0f0b" : "#fff") : t.textDim, position: "absolute", top: 3, left: on ? 19 : 3, transition: "left 0.2s" }} />
    </div>
  );
}

// ── TACTICAL UI PRIMITIVES ─────────────────────────────────────────────────
// Vocabulário visual compartilhado (cantos de mira, rótulos monoespaçados,
// barra de progresso segmentada, relógio ao vivo) usado para dar identidade
// "tática" consistente a cards e painéis pelo app inteiro.

// Cantos de mira — 4 brackets absolutos nos cantos de um container `position:relative`.
export function TacCorners({ color, size = 12, thickness = 2, inset = 0, opacity = 0.55, zIndex }) {
  const t = useTheme();
  if (t && t.corners === false) return null;          // estilos sem cantos de mira (Foco calmo, Institucional)
  const base = { position: "absolute", width: size, height: size, pointerEvents: "none", zIndex };
  return (
    <>
      <span style={{ ...base, top: inset, left: inset, borderTop: `${thickness}px solid ${color}`, borderLeft: `${thickness}px solid ${color}`, opacity }} />
      <span style={{ ...base, top: inset, right: inset, borderTop: `${thickness}px solid ${color}`, borderRight: `${thickness}px solid ${color}`, opacity }} />
      <span style={{ ...base, bottom: inset, left: inset, borderBottom: `${thickness}px solid ${color}`, borderLeft: `${thickness}px solid ${color}`, opacity }} />
      <span style={{ ...base, bottom: inset, right: inset, borderBottom: `${thickness}px solid ${color}`, borderRight: `${thickness}px solid ${color}`, opacity }} />
    </>
  );
}

// Rótulo de seção estilo HUD: tick + mono uppercase tracked.
export function TacEyebrow({ children, color, style }) {
  const t = useTheme();
  const c = color || t.textDim;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", ...style }}>
      <span style={{ width: 6, height: 1.5, background: c, opacity: 0.75, flexShrink: 0 }} />
      <span className="g17-mono" style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: c }}>{children}</span>
    </div>
  );
}

// Barra de progresso segmentada (mesmo vocabulário da vinheta de abertura).
export function TickBar({ value = 0, max = 100, segments = 24, color, height = 6, bg }) {
  const t = useTheme();
  const c = color || t.ac;
  const filled = Math.round((Math.min(max, Math.max(0, value)) / (max || 1)) * segments);
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {Array.from({ length: segments }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height, borderRadius: 1,
          background: i < filled ? c : (bg || t.bgInner),
          boxShadow: i < filled ? `0 0 5px ${c}90` : "none",
          transition: "background 0.25s ease, box-shadow 0.25s ease",
        }} />
      ))}
    </div>
  );
}

// Relógio ao vivo, isolado em componente próprio para não re-renderizar o app inteiro a cada segundo.
export function LiveClock({ color, fontSize }) {
  const t = useTheme();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <span className="g17-mono tac-num" style={{ fontSize: fontSize || "0.78rem", letterSpacing: "0.04em", color: color || t.textDim, fontWeight: 600 }}>
      {hh}<span style={{ opacity: 0.45 }}>:</span>{mm}<span style={{ opacity: 0.45 }}>:</span>{ss}
    </span>
  );
}

export function ContestEmblem({ id, size = 32, fallback, style }) {
  const [broken, setBroken] = useState(false);
  if (CONTEST_EMBLEMS[id] && !broken) {
    return <img src={`${process.env.PUBLIC_URL || ""}/emblems/${id}.${CONTEST_EMBLEMS[id] === 1 ? "webp" : CONTEST_EMBLEMS[id]}`} alt="" width={size} height={size} loading="lazy" decoding="async"
      onError={() => setBroken(true)}
      style={{ width:size, height:size, objectFit:"contain", display:"block", flexShrink:0, ...style }} />;
  }
  const known = CONTEST_LIST.find(c => c.id === id);
  return <span aria-hidden="true" style={{ width:size, height:size, fontSize:size*0.62, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, ...style }}>{fallback || known?.icon || "📋"}</span>;
}

export function StarLegendModal({ onClose, theme: t }) {
  return (
    <div style={{position:"fixed",inset:0,background:"#00000070",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:14,padding:"1.5rem",width:460,maxWidth:"92vw",boxShadow:"0 20px 60px #00000070"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1.2rem"}}>
          <h3 style={{margin:0,fontSize:"1rem",fontWeight:700,color:t.text}}>Níveis</h3>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:t.textDim,cursor:"pointer",fontSize:"1.1rem",lineHeight:1}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.8rem"}}>
          {STAR_LEVELS.map(l=>(
            <div key={l.stars} style={{display:"grid",gridTemplateColumns:"7rem 1fr",gap:"0.6rem",alignItems:"start"}}>
              <span style={{fontWeight:700,color:t.ac,fontSize:"0.82rem",lineHeight:1.4}}>{l.label}</span>
              <span style={{color:t.textMid,fontSize:"0.82rem",lineHeight:1.5}}>{l.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StarRating({ value, onChange, showHelp }) {
  const t = useTheme();
  const [hover, setHover]       = useState(0);
  const [showModal, setShowModal] = useState(false);
  return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      {[1,2,3,4,5].map(i=>(
        <span key={i} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(0)} onClick={()=>onChange(i)}
          style={{cursor:"pointer",fontSize:"1rem",color:i<=(hover||value)?t.ac:t.border,transition:"color 0.1s",lineHeight:1}}>★</span>
      ))}
      {showHelp && (
        <>
          <span onClick={()=>setShowModal(true)}
            style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${t.border}`,display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:"0.65rem",color:t.textDim,marginLeft:2,flexShrink:0,lineHeight:1}}>?</span>
          {showModal && <StarLegendModal onClose={()=>setShowModal(false)} theme={t}/>}
        </>
      )}
    </div>
  );
}

// Texto com formatação simples: "## título", "- item", "**negrito**"
export function RichText({ text, t, size = "0.84rem" }) {
  const inline = str => String(str).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i} style={{ color: t.text, fontWeight: 700 }}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>);
  const lines = String(text || "").split("\n");
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={"u" + out.length} style={{ margin: "0.2rem 0 0.7rem", paddingLeft: "1.1rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>{list}</ul>); list = []; } };
  lines.forEach((ln, i) => {
    const l = ln.trim();
    if (/^##\s+/.test(l)) { flush(); out.push(<div key={i} style={{ display: "flex", alignItems: "center", gap: "0.45rem", margin: out.length ? "1rem 0 0.4rem" : "0 0 0.4rem", fontSize: "0.95rem", fontWeight: 800, color: t.text }}><span style={{ width: 4, height: 16, borderRadius: 2, background: t.ac, flexShrink: 0 }} />{inline(l.replace(/^##\s+/, ""))}</div>); }
    else if (/^[-•]\s+/.test(l)) list.push(<li key={i} style={{ fontSize: size, color: t.textMid, lineHeight: 1.5 }}>{inline(l.replace(/^[-•]\s+/, ""))}</li>);
    else if (!l) flush();
    else { flush(); out.push(<p key={i} style={{ margin: "0 0 0.6rem", fontSize: size, color: t.textMid, lineHeight: 1.55 }}>{inline(l)}</p>); }
  });
  flush();
  return <div>{out}</div>;
}

export function SheetModal({ title, subtitle, onClose, footer, children, maxWidth = 620, labelledBy }) {
  const t = useTheme();
  const isMob = useIsMobile();
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:120, background:"rgba(0,0,0,0.62)", display:"flex", alignItems: isMob ? "flex-end" : "center", justifyContent:"center", padding: isMob ? 0 : "1.5rem" }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby={labelledBy}
        style={{ width:"100%", maxWidth, maxHeight: isMob ? "92dvh" : "88vh", display:"flex", flexDirection:"column", background:t.bgCard, border:`1px solid ${t.border}`,
          borderRadius: isMob ? "18px 18px 0 0" : 18, boxShadow:"0 24px 60px rgba(0,0,0,0.5)", overflow:"hidden" }}>
        <div style={{ padding: isMob ? "1rem 1.1rem 0.6rem" : "1.3rem 1.5rem 0.7rem", borderBottom:`1px solid ${t.borderIn}` }}>
          <h2 id={labelledBy} style={{ margin:0, fontSize: isMob ? "1.2rem" : "1.35rem", fontWeight:800, color:t.text, letterSpacing:"-0.02em" }}>{title}</h2>
          {subtitle && <p style={{ margin:"0.3rem 0 0", fontSize:"0.84rem", color:t.textDim, lineHeight:1.45 }}>{subtitle}</p>}
        </div>
        <div style={{ overflowY:"auto", padding: isMob ? "0.9rem 1.1rem" : "1.1rem 1.5rem", flex:1, minHeight:0 }}>{children}</div>
        {footer && <div style={{ padding: isMob ? "0.8rem 1.1rem calc(0.8rem + env(safe-area-inset-bottom))" : "0.9rem 1.5rem", borderTop:`1px solid ${t.borderIn}`, display:"flex", gap:"0.6rem", justifyContent:"space-between", alignItems:"center" }}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
